import React, { useEffect, useMemo, useState } from "react";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { GridColDef } from "@mui/x-data-grid";
import { useQuery } from "@apollo/client/react";
import DataGrids from "./DataGrid";
import { GET_OFFER_LIST } from "../graphql/query";
import type { GetOfferListResponse, OfferItem } from "../interfaces/offer.interface";

const passTypeLabel: Record<string, string> = {
  DROP_IN: "Drop-in",
  WEEKLY_4_CLASSES: "Weekly",
  MONTHLY_4_CLASSES: "Monthly",
};

const formatDate = (value?: string) => {
  if (!value) return "-";
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[2]}/${match[3]}/${match[1]}`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

type AllOfferTableProps = {
  onView: (id: string) => void;
  onDelete: (id: string, name: string) => void;
  refreshTrigger?: number;
};

const AllOfferTable: React.FC<AllOfferTableProps> = ({
  onView,
  onDelete,
  refreshTrigger,
}) => {
  const [inputSearch, setInputSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });

  const { data, loading, refetch, previousData } = useQuery<GetOfferListResponse>(GET_OFFER_LIST, {
    variables: {
      page: paginationModel.page + 1,
      perPage: paginationModel.pageSize,
      search: searchQuery || "",
    },
    fetchPolicy: "cache-and-network",
  });

  // Debounce: fire backend query 400ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(inputSearch);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [inputSearch]);

  useEffect(() => {
    if (refreshTrigger) {
      refetch();
    }
  }, [refreshTrigger, refetch]);

  const rows = useMemo(() => {
    const list: OfferItem[] = data?.getOfferList?.data ?? [];
    return list.map((row, index) => ({
      ...row,
      serial: String(paginationModel.page * paginationModel.pageSize + index + 1).padStart(2, "0"),
      validDates: `${formatDate(row.validFrom)} To ${formatDate(row.validTill)}`,
    }));
  }, [data, paginationModel.page, paginationModel.pageSize]);

  const rowCount =
    data?.getOfferList?.totalCount ??
    previousData?.getOfferList?.totalCount ??
    0;

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: "serial", headerName: "S.No", minWidth: 50, flex: 0.3, sortable: false },
      { field: "offerName", headerName: "Offer Name", minWidth: 130, flex: 1.0 },
      {
        field: "offerCode",
        headerName: "Offer Code",
        minWidth: 110,
        flex: 0.7,
        renderCell: (params) => (
          <Chip
            label={params.row.offerCode || "-"}
            size="small"
            sx={{
              fontWeight: 600,
              borderRadius: "8px",
              border: "1px solid #E1E5EC",
              backgroundColor: "#F9FAFB",
            }}
          />
        ),
      },
      {
        field: "discount",
        headerName: "Discount",
        minWidth: 90,
        flex: 0.6,
        valueGetter: (params: any) =>
          `${parseFloat(Number(params.row.discount || 0).toFixed(2))}%`,
        renderCell: (params) => (
          <Typography sx={{ color: "#22C55E", fontWeight: 700 }}>
            {`${parseFloat(Number(params.row.discount || 0).toFixed(2))}%`}
          </Typography>
        ),
      },
      { field: "validDates", headerName: "Valid Dates", minWidth: 180, flex: 1.1 },
      {
        field: "passType",
        headerName: "Applicable Passes",
        minWidth: 120,
        flex: 0.7,
        valueGetter: (params: any) => passTypeLabel[params.row.passType] || params.row.passType || "-",
      },
      {
        field: "actions",
        headerName: "Actions",
        minWidth: 110,
        flex: 0.6,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <IconButton
              size="small"
              onClick={() => onView(params.row.id)}
              sx={{ border: "1px solid #D1E7C7", color: "#7CB342", width: 28, height: 28 }}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onDelete(params.row.id, params.row.offerName)}
              sx={{ border: "1px solid #F2B6B6", color: "#E53935", width: 28, height: 28 }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    [onDelete, onView],
  );

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      <DataGrids
        rows={rows}
        columns={columns}
        toolbar
        searchQuery={inputSearch}
        onSearchQueryChange={setInputSearch}
        toolbarShowAddIcon={false}
        paginationMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[5, 10, 20]}
        getRowId={(row: any) => row.id}
        onMobileView={(row: any) => onView(row.id)}
        onMobileDelete={(row: any) => onDelete(row.id, row.offerName)}
        loading={loading}
        sx={{
          height: { xs: "auto", sm: "60vh", md: "60vh" },
          minHeight: 320,
          width: "100%",
          maxWidth: "100%",
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: "#fff",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#F9FAFB",
            borderBottom: "1px solid #E5E7EB",
          },
          "& .MuiDataGrid-cell": {
            whiteSpace: "nowrap",
          },
        }}
      />
    </Box>
  );
};

export default AllOfferTable;
